// Steuerberater-Export: CSV im Format für österreichische Buchhaltungssoftware
// (BMD NTCS / RZL / Lexware-kompatibel)

import { type Rechnung } from '../types';
import { type EuroCents } from '../../../types/branded';

function centsToDecimal(cents: EuroCents): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function csvEscape(s: string): string {
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const KONTO_MAP: Record<string, string> = {
  VGÜ:      '4000',   // Erlöse VGÜ (steuerfrei)
  Beratung: '4300',   // Erlöse Beratung (20% USt)
  Begehung: '4300',
  Schulung: '4300',
  Gutachten:'4200',   // Erlöse Gutachten
  Sonstige: '4900',
};

import { LEISTUNG_MAP } from '../data/leistungskatalog';

function getKonto(leistungCode: string): string {
  const l = LEISTUNG_MAP.get(leistungCode);
  if (l === undefined) { return '4900'; }
  return KONTO_MAP[l.typ] ?? '4900';
}

// ── CSV generieren ────────────────────────────────────────────────────────

export function generateSteuerberaterCSV(rechnungen: Rechnung[]): string {
  const header = [
    'Datum', 'Belegart', 'Belegnummer', 'Buchungstext',
    'Brutto', 'Netto', 'USt', 'USt-Satz', 'Konto', 'Mandant',
  ].map(csvEscape).join(';');

  const rows: string[] = rechnungen.flatMap(r => {
    // Pro Rechnung eine Zeile pro USt-Gruppe
    const ustGruppen = new Map<number, { netto: EuroCents; ust: EuroCents; codes: string[] }>();
    for (const p of r.positionen) {
      const g = ustGruppen.get(p.ustSatz) ?? { netto: 0 as EuroCents, ust: 0 as EuroCents, codes: [] };
      ustGruppen.set(p.ustSatz, {
        netto: (g.netto + p.gesamtNetto) as EuroCents,
        ust:   (g.ust   + p.gesamtUst)  as EuroCents,
        codes: [...g.codes, p.leistungCode],
      });
    }

    return [...ustGruppen.entries()].map(([satz, g]) => {
      const konto = getKonto(g.codes[0] ?? '');
      const brutto = (g.netto + g.ust) as EuroCents;
      return [
        r.datum,
        'AR',
        r.rechnungsNr,
        csvEscape(`${r.empfaenger.firma} – ${r.rechnungsNr}`),
        centsToDecimal(brutto),
        centsToDecimal(g.netto),
        centsToDecimal(g.ust),
        satz.toFixed(0),
        konto,
        r.mandantId,
      ].join(';');
    });
  });

  return [header, ...rows].join('\r\n');
}

// ── Download ──────────────────────────────────────────────────────────────

export function downloadSteuerberaterCSV(rechnungen: Rechnung[], zeitraum: string): void {
  const csv  = generateSteuerberaterCSV(rechnungen);
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }); // BOM für Excel
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `Buchungen_${zeitraum.replace(/\s/g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
